import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { handleUnauthorized } from './auth-expiry';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/core';

// Client metadata sent on every request so the backend can capture/log
// X-Client-Type (web|mobile) and X-Client-Info (app + platform + language).
const CLIENT_TYPE = 'web';

/** A per-request correlation id. `randomUUID` needs a secure context, so the
 *  fallback keeps the header present on plain http during development. */
export const newTraceId = (): string => {
  try {
    return crypto.randomUUID();
  } catch {
    return `t-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
};
const CLIENT_INFO = (() => {
  try {
    const nav = navigator as Navigator & { platform?: string };
    return ['4sCoreASTRUM-web', nav.platform, nav.language]
      .filter(Boolean)
      .join('; ')
      .slice(0, 480);
  } catch {
    return '4sCoreASTRUM-web';
  }
})();

// Apply to ALL axios instances (auditlog-api, file-upload, notifications-vs, …) via
// the shared global defaults, so every outbound request carries the client headers.
axios.defaults.headers.common['X-Client-Type'] = CLIENT_TYPE;
axios.defaults.headers.common['X-Client-Info'] = CLIENT_INFO;

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = localStorage.getItem('accessToken');
        if (config.headers) {
          if (token) config.headers.Authorization = `Bearer ${token}`;
          config.headers['X-Client-Type'] = CLIENT_TYPE;
          config.headers['X-Client-Info'] = CLIENT_INFO;
          // The correlation middleware HONOURS an inbound trace id, so minting it
          // here means the caller knows it before the response arrives — which is
          // what a "this answer was wrong" control needs on a surface that keeps
          // no run record, and what a failed request needs when no response
          // header is ever written. A caller that sets its own wins.
          if (!config.headers['x-trace-id']) {
            config.headers['x-trace-id'] = newTraceId();
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        // Expired/invalid session → clean logout (clears both tokens + redirects).
        if (error.response?.status === 401) {
          handleUnauthorized();
        }
        return Promise.reject(error);
      }
    );
  }

  getApi() {
    return this.api;
  }
}

export const apiService = new ApiService();
export const api = apiService.getApi();
